import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Alert,
} from "@mui/material";

export default function InvoiceRequestModal({
  open,
  onClose,
  selectedPackage, // { id, label, students, billing }
  onSubmit,        // async (payload) => void
}) {
  const [form, setForm] = useState({
    schoolName: "",
    contactName: "",
    role: "",
    email: "",
    phone: "",
    address: "",
    postcode: "",
    financeEmail: "",
    poNumber: "",
    notes: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!open) return;
    setSuccessMsg("");
    setErrorMsg("");
    setSubmitting(false);
  }, [open]);

  const pkgText = useMemo(() => {
  if (!selectedPackage) return "";

  const label =
    selectedPackage.planLabel ||
    selectedPackage.label ||
    selectedPackage.packageKey ||
    "School package";

  const seats =
    typeof selectedPackage.seats === "number"
      ? `${selectedPackage.seats} seats`
      : typeof selectedPackage.students === "number"
      ? `${selectedPackage.students} seats`
      : "—";

  const billing =
    selectedPackage.billingInterval ||
    selectedPackage.billing ||
    "annual";

  return `${label} • ${seats} • ${billing} billing`;
}, [selectedPackage]);


  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const basicValid =
    form.schoolName.trim() &&
    form.contactName.trim() &&
    form.email.trim();

  const handleSubmit = async () => {
    setErrorMsg("");
    setSuccessMsg("");

    if (!basicValid) {
      setErrorMsg("Please fill in School Name, Contact Name and Email.");
      return;
    }

    try {
      setSubmitting(true);
      await onSubmit({
  ...form,

  // ✅ REQUIRED BY requestSchoolInvoiceHttp
  packageKey: selectedPackage.packageKey || selectedPackage.planId,
  seats:
  typeof selectedPackage.seats === "number"
    ? selectedPackage.seats
    : typeof selectedPackage.students === "number"
    ? selectedPackage.students
    : null,

  billingInterval: "annual", // PO / invoice = annual only

  // optional (admin readability only)
  planLabel:
  selectedPackage.planLabel ||
  selectedPackage.label ||
  selectedPackage.packageKey ||
  null,


  createdAtClient: new Date().toISOString(),
  source: "subscriptions_schools_section",
});


      setSuccessMsg("Thanks — we’ve received your invoice request. We’ll contact you shortly.");
    } catch (e) {
      setErrorMsg(e?.message || "Could not submit request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 800 }}>
        Request Invoice / Purchase Order (Schools)
      </DialogTitle>

      <DialogContent dividers>
        <Typography style={{ marginBottom: 10, fontFamily: "Montserrat, sans-serif" }}>
          Use this if your school can’t pay by card. We’ll invoice your finance team (PO optional).
        </Typography>

        {pkgText ? (
          <Alert severity="info" style={{ marginBottom: 14 }}>
            Selected package: <strong>{pkgText}</strong>
          </Alert>
        ) : null}

        {successMsg ? <Alert severity="success" style={{ marginBottom: 14 }}>{successMsg}</Alert> : null}
        {errorMsg ? <Alert severity="error" style={{ marginBottom: 14 }}>{errorMsg}</Alert> : null}

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField label="School name *" value={form.schoolName} onChange={update("schoolName")} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="School address" value={form.address} onChange={update("address")} fullWidth />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField label="Postcode" value={form.postcode} onChange={update("postcode")} fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Contact name *" value={form.contactName} onChange={update("contactName")} fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Role (e.g. SBM, Headteacher)" value={form.role} onChange={update("role")} fullWidth />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField label="Contact email *" value={form.email} onChange={update("email")} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Phone" value={form.phone} onChange={update("phone")} fullWidth />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Finance email (optional)"
              value={form.financeEmail}
              onChange={update("financeEmail")}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Purchase Order number (optional)"
              value={form.poNumber}
              onChange={update("poNumber")}
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Notes (optional)"
              value={form.notes}
              onChange={update("notes")}
              fullWidth
              multiline
              minRows={3}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions style={{ padding: 16 }}>
        <Button onClick={onClose} disabled={submitting} style={{ textTransform: "none" }}>
          Close
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
          style={{ textTransform: "none", fontWeight: 800 }}
        >
          {submitting ? "Submitting…" : "Submit request"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
